import { useEffect, useRef } from "react"
import { useSnapshot } from "../data/store"

const STEP_NORMAL = 0.32
const STEP_RUSH = 0.21

type Variation = {
  chords: number[][]
  melody: number[]
  bass: number[]
  lead: OscillatorType
  pad: OscillatorType
  leadVol: number
  padVol: number
}

const VARIATIONS: Variation[] = [
  {
    chords: [[48, 52, 55, 60], [45, 48, 52, 57], [43, 47, 50, 55], [48, 52, 55, 60]],
    melody: [72, 0, 76, 0, 79, 0, 76, 0, 77, 0, 74, 0, 72, 0, 0, 0, 71, 0, 74, 0, 79, 0, 74, 0, 72, 0, 76, 0, 72, 0, 0, 0],
    bass: [36, 0, 0, 0, 0, 0, 43, 0, 33, 0, 0, 0, 0, 0, 40, 0, 31, 0, 0, 0, 0, 0, 38, 0, 36, 0, 0, 0, 43, 0, 31, 0],
    lead: "sine",
    pad: "triangle",
    leadVol: 0.11,
    padVol: 0.045,
  },
  {
    chords: [[53, 57, 60, 65], [48, 52, 55, 60], [41, 45, 48, 53], [43, 47, 50, 55]],
    melody: [77, 0, 76, 74, 0, 72, 0, 74, 0, 76, 0, 79, 0, 77, 76, 0, 74, 0, 72, 0, 74, 0, 71, 0, 72, 0, 0, 0, 0, 0, 0, 0],
    bass: [41, 0, 0, 29, 0, 0, 36, 0, 31, 0, 0, 29, 0, 0, 33, 0, 36, 0, 0, 0, 43, 0, 31, 0, 29, 0, 36, 0, 31, 0, 29, 0],
    lead: "triangle",
    pad: "sine",
    leadVol: 0.075,
    padVol: 0.06,
  },
  {
    chords: [[50, 53, 57, 62], [48, 52, 55, 60], [46, 50, 53, 58], [43, 47, 50, 55]],
    melody: [74, 0, 0, 77, 0, 0, 76, 0, 74, 0, 72, 0, 74, 0, 0, 0, 79, 0, 77, 0, 76, 0, 74, 0, 72, 74, 0, 76, 0, 0, 0, 0],
    bass: [38, 0, 38, 0, 0, 0, 36, 0, 34, 0, 34, 0, 0, 0, 31, 0, 33, 0, 36, 0, 38, 0, 41, 0, 43, 0, 41, 0, 38, 0, 36, 0],
    lead: "square",
    pad: "triangle",
    leadVol: 0.035,
    padVol: 0.05,
  },
  {
    chords: [[55, 59, 62, 67], [53, 57, 60, 65], [48, 52, 55, 60], [43, 47, 50, 59]],
    melody: [79, 0, 81, 0, 83, 0, 81, 0, 79, 0, 77, 0, 76, 0, 74, 0, 76, 0, 79, 0, 81, 0, 79, 0, 76, 0, 74, 0, 72, 0, 0, 0],
    bass: [43, 0, 0, 0, 41, 0, 0, 0, 36, 0, 0, 0, 38, 0, 0, 0, 41, 0, 0, 0, 43, 0, 0, 0, 36, 0, 31, 0, 29, 0, 31, 0],
    lead: "sine",
    pad: "triangle",
    leadVol: 0.12,
    padVol: 0.04,
  },
]

let sharedCtx: AudioContext | null = null
let musicBus: GainNode | null = null
let sfxBus: GainNode | null = null
let timer: number | null = null
let step = 0
let variation = 0
let unlocked = false
let musicOn = true
let sfxOn = true
let rushOn = false
let clickSound = ""

function readStoredFlags(): { music: boolean; sfx: boolean } {
  try {
    const raw = localStorage.getItem("cb_settings_v2")
    if (!raw) return { music: true, sfx: true }
    const parsed = JSON.parse(raw) as { settings?: { music?: unknown; sfx?: unknown } }
    return {
      music: parsed.settings?.music !== false,
      sfx: parsed.settings?.sfx !== false,
    }
  } catch {
    return { music: true, sfx: true }
  }
}

function updateBusGains(): void {
  try {
    if (sharedCtx === null) return
    const t = sharedCtx.currentTime
    if (musicBus !== null) musicBus.gain.setTargetAtTime(musicOn ? 1 : 0, t, 0.02)
    if (sfxBus !== null) sfxBus.gain.setTargetAtTime(sfxOn ? 1 : 0, t, 0.02)
  } catch {
    return
  }
}

try {
  const stored = readStoredFlags()
  musicOn = stored.music
  sfxOn = stored.sfx
} catch {
  musicOn = true
  sfxOn = true
}

function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12)
}

function ensureCtx(): AudioContext | null {
  try {
    if (sharedCtx !== null) return sharedCtx
    const Ctor: typeof AudioContext | undefined = window.AudioContext !== undefined ? window.AudioContext : (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (Ctor === undefined) return null
    const ctx = new Ctor()
    const master = ctx.createGain()
    master.gain.value = 0.32
    master.connect(ctx.destination)
    const mb = ctx.createGain()
    mb.gain.value = musicOn ? 1 : 0
    mb.connect(master)
    const sb = ctx.createGain()
    sb.gain.value = sfxOn ? 1 : 0
    sb.connect(master)
    sharedCtx = ctx
    musicBus = mb
    sfxBus = sb
    return ctx
  } catch {
    return null
  }
}

function playTone(midi: number, at: number, dur: number, type: OscillatorType, vol: number): void {
  const ctx = sharedCtx
  const out = musicBus
  if (ctx === null || out === null) return
  try {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = type
    osc.frequency.value = midiToFreq(midi)
    gain.gain.setValueAtTime(0, at)
    gain.gain.linearRampToValueAtTime(vol, at + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, at + dur)
    osc.connect(gain)
    gain.connect(out)
    osc.start(at)
    osc.stop(at + dur + 0.05)
  } catch {
    return
  }
}

function playBlip(freq: number, at: number, dur: number, type: OscillatorType, vol: number): void {
  const ctx = sharedCtx
  if (ctx === null || sfxBus === null) return
  try {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = type
    osc.frequency.setValueAtTime(freq, at)
    gain.gain.setValueAtTime(0, at)
    gain.gain.linearRampToValueAtTime(vol, at + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.0001, at + dur)
    osc.connect(gain)
    gain.connect(sfxBus)
    osc.start(at)
    osc.stop(at + dur + 0.05)
  } catch {
    return
  }
}

function scheduleStep(): void {
  const ctx = sharedCtx
  if (ctx === null || musicBus === null) return
  if (!musicOn || !unlocked) return
  const v = VARIATIONS[variation % VARIATIONS.length] ?? VARIATIONS[0]
  if (v === undefined) return
  const s = step % 32
  const bar = Math.floor(s / 8) % 4
  const at = ctx.currentTime + 0.02
  const boost = rushOn ? 1.35 : 1
  const chord = v.chords[bar]
  if (s % 8 === 0 && chord !== undefined) {
    for (const note of chord) {
      playTone(note, at, rushOn ? 1.6 : 2.4, v.pad, v.padVol * boost)
    }
  }
  const bass = v.bass[s]
  if (bass !== undefined && bass !== 0) {
    playTone(bass, at, 0.5, "sine", 0.14 * boost)
  }
  const lead = v.melody[s]
  if (lead !== undefined && lead !== 0) {
    playTone(lead, at, rushOn ? 0.4 : 0.55, v.lead, v.leadVol * boost)
    playTone(lead + 12, at, 0.4, "triangle", 0.025 * boost)
    if (rushOn) {
      playTone(lead + 24, at + 0.06, 0.22, "sine", 0.05)
    }
  }
  if (rushOn && s % 2 === 0) {
    playTone(96, at, 0.08, "square", 0.012)
  }
  step += 1
  if (step % 32 === 0) {
    variation = (variation + 1) % VARIATIONS.length
  }
}

function currentStepDur(): number {
  return rushOn ? STEP_RUSH : STEP_NORMAL
}

function startTimer(): void {
  if (timer !== null) return
  const ctx = ensureCtx()
  if (ctx === null) return
  if (ctx.state === "suspended") {
    void ctx.resume().catch((): void => undefined)
  }
  scheduleStep()
  timer = window.setInterval(scheduleStep, currentStepDur() * 1000)
}

function stopTimer(): void {
  if (timer !== null) {
    window.clearInterval(timer)
    timer = null
  }
}

function restartTimer(): void {
  stopTimer()
  if (musicOn && unlocked) startTimer()
}

function unlockAudio(): void {
  const ctx = ensureCtx()
  if (ctx !== null && ctx.state === "suspended") {
    void ctx.resume().catch((): void => undefined)
  }
  unlocked = true
  updateBusGains()
  if (!musicOn) return
  if (timer === null) startTimer()
}

function silenceMusic(): void {
  stopTimer()
}

export function setClickSound(id: string | null | undefined): void {
  const v = (id ?? "").trim().toLowerCase()
  clickSound = v === "none" || v === "clear" ? "" : v
}

export function getClickSound(): string {
  return clickSound
}

function playClickVariant(at: number): void {
  const base = 300 + Math.random() * 120
  switch (clickSound) {
    case "robotic":
      playBlip(base * 0.5, at, 0.1, "square", 0.05)
      playBlip(base * 2, at + 0.03, 0.08, "square", 0.035)
      playBlip(base * 3, at + 0.06, 0.06, "sawtooth", 0.02)
      break
    case "reverb":
      playBlip(base, at, 0.14, "triangle", 0.06)
      playBlip(base, at + 0.09, 0.16, "sine", 0.04)
      playBlip(base * 0.99, at + 0.18, 0.2, "sine", 0.025)
      break
    case "bubble":
      playBlip(base * 1.2, at, 0.07, "sine", 0.07)
      playBlip(base * 1.5, at + 0.05, 0.08, "sine", 0.06)
      playBlip(base * 1.9, at + 0.1, 0.1, "triangle", 0.04)
      break
    case "arcade":
      playBlip(660, at, 0.06, "square", 0.045)
      playBlip(880, at + 0.05, 0.06, "square", 0.045)
      playBlip(1320, at + 0.1, 0.09, "square", 0.035)
      break
    case "cosmic_chime":
      playBlip(midiToFreq(84), at, 0.2, "sine", 0.07)
      playBlip(midiToFreq(91), at + 0.06, 0.24, "sine", 0.05)
      playBlip(midiToFreq(96), at + 0.12, 0.3, "triangle", 0.03)
      break
    case "classic_pop":
    case "":
    default:
      playBlip(base, at, 0.12, "triangle", 0.06)
      playBlip(base * 1.5, at + 0.02, 0.09, "sine", 0.04)
      break
  }
}

export function previewClickSound(id?: string): void {
  if (!sfxOn) return
  unlockAudio()
  const ctx = ensureCtx()
  if (ctx === null) return
  if (ctx.state === "suspended") {
    void ctx.resume().catch((): void => undefined)
  }
  const prev = clickSound
  if (id !== undefined) {
    const v = id.trim().toLowerCase()
    clickSound = v === "none" || v === "clear" ? "" : v
  }
  try {
    playClickVariant(ctx.currentTime + 0.01)
  } finally {
    clickSound = prev
  }
}

export function sfxClick(): void {
  if (!sfxOn) return
  unlockAudio()
  const ctx = ensureCtx()
  if (ctx === null) return
  if (ctx.state === "suspended") {
    void ctx.resume().catch((): void => undefined)
  }
  playClickVariant(ctx.currentTime + 0.01)
}

export function applyMusicSetting(on: boolean): void {
  musicOn = on
  updateBusGains()
  if (!on) {
    silenceMusic()
  } else {
    unlockAudio()
  }
}

export function applySfxSetting(on: boolean): void {
  sfxOn = on
  updateBusGains()
}

export function sfxBuy(): void {
  if (!sfxOn) return
  unlockAudio()
  const ctx = ensureCtx()
  if (ctx === null) return
  if (ctx.state === "suspended") {
    void ctx.resume().catch((): void => undefined)
  }
  const at = ctx.currentTime + 0.01
  playBlip(midiToFreq(76), at, 0.16, "sine", 0.16)
  playBlip(midiToFreq(81), at + 0.09, 0.22, "sine", 0.16)
  playBlip(midiToFreq(88), at + 0.18, 0.3, "triangle", 0.1)
}

export function sfxFanfare(): void {
  if (!sfxOn) return
  unlockAudio()
  const ctx = ensureCtx()
  if (ctx === null) return
  if (ctx.state === "suspended") {
    void ctx.resume().catch((): void => undefined)
  }
  const at = ctx.currentTime + 0.01
  const seq = [72, 76, 79, 84]
  seq.forEach((m, i) => {
    playBlip(midiToFreq(m), at + i * 0.11, 0.28, "triangle", 0.16)
    playBlip(midiToFreq(m + 12), at + i * 0.11, 0.2, "sine", 0.07)
  })
}

export function sfxAbility(): void {
  if (!sfxOn) return
  unlockAudio()
  const ctx = ensureCtx()
  if (ctx === null) return
  if (ctx.state === "suspended") {
    void ctx.resume().catch((): void => undefined)
  }
  const at = ctx.currentTime + 0.01
  playBlip(midiToFreq(67), at, 0.14, "triangle", 0.14)
  playBlip(midiToFreq(74), at + 0.08, 0.16, "triangle", 0.14)
  playBlip(midiToFreq(79), at + 0.16, 0.24, "sine", 0.16)
  playBlip(midiToFreq(91), at + 0.24, 0.32, "sine", 0.1)
}

export function sfxJoin(): void {
  if (!sfxOn) return
  unlockAudio()
  const ctx = ensureCtx()
  if (ctx === null) return
  if (ctx.state === "suspended") {
    void ctx.resume().catch((): void => undefined)
  }
  const at = ctx.currentTime + 0.01
  playBlip(midiToFreq(72), at, 0.14, "sine", 0.08)
  playBlip(midiToFreq(76), at + 0.09, 0.22, "sine", 0.08)
}

export function sfxLeave(): void {
  if (!sfxOn) return
  unlockAudio()
  const ctx = ensureCtx()
  if (ctx === null) return
  if (ctx.state === "suspended") {
    void ctx.resume().catch((): void => undefined)
  }
  const at = ctx.currentTime + 0.01
  playBlip(midiToFreq(76), at, 0.14, "sine", 0.08)
  playBlip(midiToFreq(72), at + 0.09, 0.22, "sine", 0.08)
}

export function unlockAudioOnce(): void {
  unlockAudio()
}

export function useBakeryMusic(rush?: boolean): { enabled: boolean; sfx: boolean } {
  const { state } = useSnapshot()
  const enabled = state.settings.music
  const sfx = state.settings.sfx
  const rushFlag = rush ?? false
  const enabledRef = useRef(enabled)
  const sfxRef = useRef(sfx)
  const rushRef = useRef(rushFlag)

  useEffect(() => {
    enabledRef.current = enabled
    musicOn = enabled
    updateBusGains()
    if (!enabled) {
      silenceMusic()
    } else if (unlocked) {
      unlockAudio()
    }
  }, [enabled])

  useEffect(() => {
    sfxRef.current = sfx
    sfxOn = sfx
    updateBusGains()
  }, [sfx])

  useEffect(() => {
    const prev = rushRef.current
    rushRef.current = rushFlag
    rushOn = rushFlag
    if (prev !== rushFlag) {
      restartTimer()
    }
  }, [rushFlag])

  useEffect(() => {
    musicOn = enabledRef.current
    sfxOn = sfxRef.current
    rushOn = rushRef.current
    const onUnlock = () => unlockAudio()
    window.addEventListener("pointerdown", onUnlock)
    window.addEventListener("keydown", onUnlock)
    window.addEventListener("touchend", onUnlock)
    return () => {
      window.removeEventListener("pointerdown", onUnlock)
      window.removeEventListener("keydown", onUnlock)
      window.removeEventListener("touchend", onUnlock)
    }
  }, [])

  return { enabled, sfx }
}
